import { zodResolver } from '@hookform/resolvers/zod';
import { useMainCategories } from 'features/main-category/hooks/useMainCategories';
import { Camera, Loader, SquarePen, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { z } from 'zod';
import { useUpdateSubCategory } from '../hooks/useUpdateSubCategory';
import { subCategoryServices } from '../services/sub-category.service';

const subCategorySchema = z.object({
  name: z.object({
    english: z.string().min(1, 'English name is required'),
    hindi: z.string().optional(),
    marwari: z.string().optional(),
  }),
  description: z
    .object({
      english: z
        .string()
        .optional()
        .refine((val) => {
          if (!val || val.trim() === '') return true;
          return val.length >= 250 && val.length <= 500;
        }, {
          message: 'Enter a description between 250 and 500 characters.',
        }),
      hindi: z
        .string()
        .optional()
        .refine((val) => {
          if (!val || val.trim() === '') return true;
          return val.length >= 250 && val.length <= 500;
        }, {
          message: 'Enter a description between 250 and 500 characters.',
        }),
      marwari: z
        .string()
        .optional()
        .refine((val) => {
          if (!val || val.trim() === '') return true;
          return val.length >= 250 && val.length <= 500;
        }, {
          message: 'Enter a description between 250 and 500 characters.',
        }),
    })
    .optional(),
  parentCategory: z.string().min(1, 'Parent category is required'),
  icon: z.string().optional().or(z.literal('')),
  image: z.string().optional().or(z.literal('')),
  displayOrder: z.number().min(0).optional(),
});

const EditSubCategoryModal = (props) => {
  const { isEditModalOpen, setIsEditModalOpen, category } = props;
  const { data: mainCategories } = useMainCategories({ limit: 99 });
  const { mutate: updateSubCategory, isPending } = useUpdateSubCategory();

  // Icon upload state
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [existingIcon, setExistingIcon] = useState(null);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const iconInputRef = useRef(null);

  // Image upload state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(subCategorySchema),
  });

  useEffect(() => {
    if (category) {
      reset({
        name: category.name,
        description: category.description,
        parentCategory: category.parentCategory?._id || category.parentCategory,
        displayOrder: category.displayOrder,
      });
      setExistingIcon(category.icon || null);
      setIconPreview(null);
      setIconFile(null);
      setExistingImage(category.image || null);
      setImagePreview(null);
      setImageFile(null);
    }
  }, [category, reset]);

  const validateFile = (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return false;
    }
    return true;
  };

  const handleIconSelect = (file) => {
    if (!file || !validateFile(file)) return;
    setIconFile(file);
    setExistingIcon(null);
    const reader = new FileReader();
    reader.onloadend = () => setIconPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveIcon = () => {
    setIconFile(null);
    setIconPreview(null);
    setExistingIcon(null);
    if (iconInputRef.current) iconInputRef.current.value = '';
  };

  const handleImageSelect = (file) => {
    if (!file || !validateFile(file)) return;
    setImageFile(file);
    setExistingImage(null);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setExistingImage(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleModalClose = () => {
    reset();
    setIconFile(null);
    setIconPreview(null);
    setExistingIcon(null);
    setImageFile(null);
    setImagePreview(null);
    setExistingImage(null);
    setIsEditModalOpen(false);
  };

  const onSubmit = async (data) => {
    try {
      const updatedData = { ...data };

      // Handle icon upload or removal
      if (iconFile) {
        // Upload new icon
        setIsUploadingIcon(true);
        const uploadResponse = await subCategoryServices.uploadFile(iconFile);
        updatedData.icon = uploadResponse?.data?.key || '';
        setIsUploadingIcon(false);
      } else if (existingIcon === null && category.icon) {
        // Icon was removed
        updatedData.icon = '';
      } else if (existingIcon) {
        // Keep existing icon (don't send icon field)
        delete updatedData.icon;
      }

      // Handle image upload or removal
      if (imageFile) {
        // Upload new image
        setIsUploadingImage(true);
        const uploadResponse = await subCategoryServices.uploadFile(imageFile);
        updatedData.image = uploadResponse?.data?.key || '';
        setIsUploadingImage(false);
      } else if (existingImage === null && category.image) {
        // Image was removed
        updatedData.image = '';
      } else if (existingImage) {
        // Keep existing image (don't send image field)
        delete updatedData.image;
      }

      const payload = {
        categoryId: category._id,
        data: updatedData,
      };

      updateSubCategory(payload, {
        onSuccess: () => {
          reset();
          setIconFile(null);
          setIconPreview(null);
          setExistingIcon(null);
          setImageFile(null);
          setImagePreview(null);
          setExistingImage(null);
          setIsEditModalOpen(false);
        },
      });
    } catch (error) {
      setIsUploadingIcon(false);
      setIsUploadingImage(false);
      toast.error(error?.response?.data?.message || 'Failed to upload file');
    }
  };

  if (!isEditModalOpen) return null;

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar'>
        {/* Header */}
        <div className='sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between'>
          <h2 className='text-lg font-semibold'>Edit Sub-Category</h2>
          <button onClick={handleModalClose} className='p-1 hover:bg-muted rounded-md transition-colors'>
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className='p-6 space-y-6'>
          {/* Parent Category Selection */}
          <div>
            <label className='block text-sm font-medium mb-1'>Parent Category *</label>
            <select {...register('parentCategory')} className='w-full px-3 py-2 text-sm'>
              <option value=''>Select Parent Category</option>
              {mainCategories?.data?.map((parent) => (
                <option key={parent._id} value={parent._id}>
                  {parent.name?.english}
                </option>
              ))}
            </select>
            {errors.parentCategory && <p className='text-destructive text-xs mt-1'>{errors.parentCategory.message}</p>}
          </div>

          {/* Name Section */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold text-foreground'>Name (Multilingual)</h3>

            <div>
              <label className='block text-sm font-medium mb-1'>English Name *</label>
              <input type='text' {...register('name.english')} className='w-full px-3 py-2 text-sm' />
              {errors.name?.english && <p className='text-destructive text-xs mt-1'>{errors.name.english.message}</p>}
            </div>

            <div>
              <label className='block text-sm font-medium mb-1'>Hindi Name</label>
              <input type='text' {...register('name.hindi')} className='w-full px-3 py-2 text-sm' />
            </div>

            <div>
              <label className='block text-sm font-medium mb-1'>Marwari Name</label>
              <input type='text' {...register('name.marwari')} className='w-full px-3 py-2 text-sm' />
            </div>
          </div>

          {/* Description Section */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold text-foreground'>Description (Multilingual)</h3>

            <div>
              <label className='block text-sm font-medium mb-1'>English Description</label>
              <textarea
                {...register('description.english')}
                rows={3}
                className='w-full px-3 py-2 text-sm'
                placeholder='Enter description between 250 and 500 characters'
              />
              {errors.description?.english && (
                <p className='text-destructive text-xs mt-1'>{errors.description.english.message}</p>
              )}
            </div>

            <div>
              <label className='block text-sm font-medium mb-1'>Hindi Description</label>
              <textarea {...register('description.hindi')} rows={3} className='w-full px-3 py-2 text-sm' />
              {errors.description?.hindi && (
                <p className='text-destructive text-xs mt-1'>{errors.description.hindi.message}</p>
              )}
            </div>

            <div>
              <label className='block text-sm font-medium mb-1'>Marwari Description</label>
              <textarea {...register('description.marwari')} rows={3} className='w-full px-3 py-2 text-sm' />
              {errors.description?.marwari && (
                <p className='text-destructive text-xs mt-1'>{errors.description.marwari.message}</p>
              )}
            </div>
          </div>

          {/* Media & Display */}
          <div className='space-y-4'>
            <h3 className='text-sm font-semibold text-foreground'>Media & Display</h3>

            {/* Icon Upload */}
            {/* <div>
              <label className='block text-sm font-medium mb-2'>Icon</label>
              {iconPreview || existingIcon ? (
                <div className='flex items-center space-x-4'>
                  <img
                    src={iconPreview || existingIcon}
                    alt='Icon preview'
                    className='w-16 h-16 rounded-lg object-cover border border-border'
                  />
                  <div className='flex flex-col space-y-2'>
                    <p className='text-xs text-muted-foreground'>{iconFile?.name || 'Current icon'}</p>
                    <div className='flex space-x-2'>
                      <button
                        type='button'
                        onClick={() => iconInputRef.current?.click()}
                        className='flex items-center space-x-1 text-xs text-primary hover:text-primary/80'
                      >
                        <Camera size={14} />
                        <span>Change</span>
                      </button>
                      <button
                        type='button'
                        onClick={handleRemoveIcon}
                        className='flex items-center space-x-1 text-xs text-destructive hover:text-destructive/80'
                      >
                        <Trash2 size={14} />
                        <span>Remove</span>
                      </button>
                    </div>
                  </div>
                  <input
                    ref={iconInputRef}
                    type='file'
                    accept='image/*'
                    onChange={(e) => handleIconSelect(e.target.files?.[0])}
                    className='hidden'
                  />
                </div>
              ) : (
                <div
                  onClick={() => iconInputRef.current?.click()}
                  className='border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors'
                >
                  <input
                    ref={iconInputRef}
                    type='file'
                    accept='image/*'
                    onChange={(e) => handleIconSelect(e.target.files?.[0])}
                    className='hidden'
                  />
                  <div className='flex flex-col items-center space-y-2'>
                    <div className='w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center'>
                      <Camera size={20} className='text-primary' />
                    </div>
                    <p className='text-sm text-muted-foreground'>Click to upload icon</p>
                    <p className='text-xs text-muted-foreground'>PNG, JPG up to 5MB</p>
                  </div>
                </div>
              )}
              {errors.icon && <p className='text-destructive text-xs mt-1'>{errors.icon.message}</p>}
            </div> */}

            {/* Image Upload */}
            <div>
              <label className='block text-sm font-medium mb-2'>Image</label>
              {imagePreview || existingImage ? (
                <div className='flex items-center space-x-4'>
                  <img
                    src={imagePreview || existingImage}
                    alt='Image preview'
                    className='w-24 h-24 rounded-lg object-cover border border-border'
                  />
                  <div className='flex flex-col space-y-2'>
                    <p className='text-xs text-muted-foreground'>{imageFile?.name || 'Current image'}</p>
                    <div className='flex space-x-2'>
                      <button
                        type='button'
                        onClick={() => imageInputRef.current?.click()}
                        className='flex items-center space-x-1 text-xs text-primary hover:text-primary/80'
                      >
                        <Camera size={14} />
                        <span>Change</span>
                      </button>
                      <button
                        type='button'
                        onClick={handleRemoveImage}
                        className='flex items-center space-x-1 text-xs text-destructive hover:text-destructive/80'
                      >
                        <Trash2 size={14} />
                        <span>Remove</span>
                      </button>
                    </div>
                  </div>
                  <input
                    ref={imageInputRef}
                    type='file'
                    accept='image/*'
                    onChange={(e) => handleImageSelect(e.target.files?.[0])}
                    className='hidden'
                  />
                </div>
              ) : (
                <div
                  onClick={() => imageInputRef.current?.click()}
                  className='border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors'
                >
                  <input
                    ref={imageInputRef}
                    type='file'
                    accept='image/*'
                    onChange={(e) => handleImageSelect(e.target.files?.[0])}
                    className='hidden'
                  />
                  <div className='flex flex-col items-center space-y-2'>
                    <div className='w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center'>
                      <Camera size={20} className='text-primary' />
                    </div>
                    <p className='text-sm text-muted-foreground'>Click to upload image</p>
                    <p className='text-xs text-muted-foreground'>PNG, JPG up to 5MB</p>
                  </div>
                </div>
              )}
              {errors.image && <p className='text-destructive text-xs mt-1'>{errors.image.message}</p>}
            </div>
          </div>

          {/* Actions */}
          <div className='flex justify-end space-x-3 pt-4 border-t border-border'>
            <button
              type='button'
              onClick={handleModalClose}
              className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={isSubmitting || isPending || isUploadingIcon || isUploadingImage}
              className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50'
            >
              {isPending || isUploadingIcon || isUploadingImage ? (
                <div className='flex items-center justify-center'>
                  <Loader size={16} className='animate-spin mr-2' />
                  {isUploadingIcon || isUploadingImage ? 'Uploading...' : 'Updating...'}
                </div>
              ) : (
                <div className='flex items-center justify-center'>
                  <SquarePen size={16} className='mr-2' />
                  Update Sub-Category
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSubCategoryModal;
